import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import { XMarkIcon, ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import RichTextEditor from './RichTextEditor';
import { fetchAssignmentGroupMembers } from '../utils/groupMembers';

interface DuplicateServiceRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceRequest: any;
  onSuccess?: (duplicatedServiceRequest: any) => void;
}

const DuplicateServiceRequestModal: React.FC<DuplicateServiceRequestModalProps> = ({
  isOpen,
  onClose,
  serviceRequest,
  onSuccess
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Search queries for Comboboxes
  const [customerQuery, setCustomerQuery] = useState('');
  const [contactQuery, setContactQuery] = useState('');
  const [engagementQuery, setEngagementQuery] = useState('');
  const [servicePlanQuery, setServicePlanQuery] = useState('');
  const [resourceQuery, setResourceQuery] = useState('');
  const [assignmentGroupQuery, setAssignmentGroupQuery] = useState('');
  const [assignedToQuery, setAssignedToQuery] = useState('');
  const [impactQuery, setImpactQuery] = useState('');
  const [urgencyQuery, setUrgencyQuery] = useState('');

  const [formData, setFormData] = useState({
    serviceRequestState: 'new',
    impact: '',
    urgency: '',
    priority: '',
    shortDescription: '',
    description: '',
    customerId: '',
    contactId: '',
    engagementId: '',
    servicePlanId: '',
    resourceId: '',
    resourceIdValue: '',
    otherResourceNames: '',
    assignmentGroupId: '',
    assignedTo: ''
  });

  const [isOtherResource, setIsOtherResource] = useState(false);
  // Store resource object separately for reliable display before resources load
  const [selectedResource, setSelectedResource] = useState<{ id: string; name: string; resourceId: string } | null>(null);

  // Impact and Urgency options
  const impactOptions = [
    { value: 'high_1', label: 'High' },
    { value: 'medium_2', label: 'Medium' },
    { value: 'low_3', label: 'Low' }
  ];

  const urgencyOptions = [
    { value: 'high_1', label: 'High' },
    { value: 'medium_2', label: 'Medium' },
    { value: 'low_3', label: 'Low' }
  ];

  // Fetch customers
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    }
  });

  // Fetch engagements
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements'],
    queryFn: async () => {
      const response = await api.get('/engagements');
      return response.data;
    }
  });

  // Fetch contacts based on customer
  const { data: contactsData } = useQuery({
    queryKey: ['contacts', formData.customerId],
    queryFn: async () => {
      if (!formData.customerId) return { users: [] };
      const params = new URLSearchParams({
        class: 'contact',
        customerId: formData.customerId,
        limit: '100'
      });
      const response = await api.get(`/users?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Fetch primary contact for auto-selection
  const { data: primaryContactData } = useQuery({
    queryKey: ['primary-contact', formData.customerId, formData.engagementId],
    queryFn: async () => {
      if (!formData.customerId) return { contact: null };
      const params = new URLSearchParams({ customerId: formData.customerId });
      if (formData.engagementId) {
        params.append('engagementId', formData.engagementId);
      }
      const response = await api.get(`/contacts/primary?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Fetch service plans based on engagement
  const { data: servicePlansData } = useQuery({
    queryKey: ['service-plans', formData.engagementId],
    queryFn: async () => {
      if (!formData.engagementId) return { plans: [] };
      const response = await api.get(`/engagements/${formData.engagementId}/service-plans`);
      return response.data;
    },
    enabled: !!formData.engagementId
  });

  // Fetch assignment groups
  const { data: groupsData } = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      const response = await api.get('/groups', { params: { limit: 1000 } });
      return response.data;
    }
  });

  // Fetch resources based on engagement
  const { data: resourcesData } = useQuery({
    queryKey: ['resources', formData.engagementId],
    queryFn: async () => {
      if (!formData.engagementId) return [];
      const response = await api.get(`/resources?engagementId=${formData.engagementId}`);
      return Array.isArray(response.data) ? response.data : (response.data.resources || []);
    },
    enabled: !!formData.engagementId
  });

  // Fetch users based on assignment group
  const { data: usersData } = useQuery({
    queryKey: ['group-users', formData.assignmentGroupId, formData.customerId],
    queryFn: async () => {
      return fetchAssignmentGroupMembers(formData.assignmentGroupId, formData.customerId || null);
    },
    enabled: !!formData.assignmentGroupId
  });

  // Priority calculation based on Impact and Urgency
  const calculatePriority = (impact: string, urgency: string): string => {
    // Normalize values to handle both 'low' and 'low_3' formats
    const normalizeValue = (value: string): string => {
      if (!value) return 'low';
      if (value.startsWith('low')) return 'low';
      if (value.startsWith('medium')) return 'medium';
      if (value.startsWith('high')) return 'high';
      return value;
    };

    const normalizedImpact = normalizeValue(impact);
    const normalizedUrgency = normalizeValue(urgency);

    // Priority Matrix:
    // Impact\Urgency | High | Medium | Low
    // High           | 1    | 2      | 3
    // Medium         | 2    | 3      | 4
    // Low            | 3    | 4      | 5

    const matrix: Record<string, Record<string, string>> = {
      'high': { 'high': 'critical_1', 'medium': 'high_2', 'low': 'moderate_3' },
      'medium': { 'high': 'high_2', 'medium': 'moderate_3', 'low': 'low_4' },
      'low': { 'high': 'moderate_3', 'medium': 'low_4', 'low': 'planning_5' }
    };

    return matrix[normalizedImpact]?.[normalizedUrgency] || 'moderate_3';
  };

  // Auto-calculate priority when impact or urgency changes
  useEffect(() => {
    if (formData.impact && formData.urgency) {
      const calculatedPriority = calculatePriority(formData.impact, formData.urgency);
      setFormData(prev => ({ ...prev, priority: calculatedPriority }));
    }
  }, [formData.impact, formData.urgency]);

  // Find and set resource when resourceId, resourceIdValue, or resources change
  useEffect(() => {
    if (resourcesData && !isOtherResource && (formData.resourceId || formData.resourceIdValue)) {
      const resourcesArray = Array.isArray(resourcesData) ? resourcesData : [];

      // Try to find by resourceId (UUID) first
      let resource = formData.resourceId
        ? resourcesArray.find((r: any) => r.id === formData.resourceId)
        : null;

      // If not found by ID, try by resourceIdValue (subscription string)
      if (!resource && formData.resourceIdValue) {
        resource = resourcesArray.find((r: any) => r.resourceId === formData.resourceIdValue);
      }

      if (resource) {
        setSelectedResource({
          id: resource.id,
          name: resource.name || '',
          resourceId: resource.resourceId || ''
        });
        // Update formData if resourceId wasn't set or doesn't match
        if (!formData.resourceId || formData.resourceId !== resource.id) {
          setFormData(prev => ({
            ...prev,
            resourceId: resource.id,
            resourceIdValue: resource.resourceId || prev.resourceIdValue
          }));
        } else if (!formData.resourceIdValue && resource.resourceId) {
          setFormData(prev => ({ ...prev, resourceIdValue: resource.resourceId || '' }));
        }
      }
    } else if (!formData.resourceId && !isOtherResource) {
      // Clear resourceIdValue when resource is cleared
      setFormData(prev => ({ ...prev, resourceIdValue: '' }));
      setSelectedResource(null);
    }
  }, [formData.resourceId, formData.resourceIdValue, resourcesData, isOtherResource]);

  // Auto-select primary contact when customer/engagement changes
  useEffect(() => {
    if (formData.customerId && primaryContactData?.contact && !formData.contactId) {
      setFormData(prev => ({
        ...prev,
        contactId: primaryContactData.contact.id
      }));
    }
  }, [formData.customerId, formData.engagementId, primaryContactData, formData.contactId]);

  // Pre-fill form when service request data changes
  useEffect(() => {
    if (serviceRequest && isOpen) {
      const hasOtherResourceNames = !!serviceRequest.otherResourceNames;
      setIsOtherResource(hasOtherResourceNames);

      setFormData({
        serviceRequestState: 'new',
        impact: serviceRequest.impact || '',
        urgency: serviceRequest.urgency || '',
        priority: serviceRequest.priority || '',
        shortDescription: `Copy of Service Request for ${serviceRequest.number || 'SR'}`,
        description: serviceRequest.description || '',
        customerId: serviceRequest.customerId || '',
        contactId: serviceRequest.contactId || '',
        engagementId: serviceRequest.engagementId || '',
        servicePlanId: serviceRequest.servicePlanId || '',
        resourceId: hasOtherResourceNames ? '' : (serviceRequest.resourceId || serviceRequest.Resource?.id || ''),
        resourceIdValue: hasOtherResourceNames ? '' : (serviceRequest.resourceIdValue || serviceRequest.Resource?.resourceId || ''),
        otherResourceNames: serviceRequest.otherResourceNames || '',
        assignmentGroupId: serviceRequest.assignmentGroupId || '',
        assignedTo: ''
      });

      // Set selected resource from serviceRequest's Resource (backend returns Resource, not EngagementResource)
      if (!hasOtherResourceNames && serviceRequest.Resource) {
        setSelectedResource({
          id: serviceRequest.Resource.id,
          name: serviceRequest.Resource.name || '',
          resourceId: serviceRequest.Resource.resourceId || ''
        });
      } else {
        setSelectedResource(null);
      }

      setErrorMessage(null);
      setValidationErrors({});
      // Reset search queries
      setCustomerQuery('');
      setContactQuery('');
      setEngagementQuery('');
      setServicePlanQuery('');
      setResourceQuery('');
      setAssignmentGroupQuery('');
      setAssignedToQuery('');
      setImpactQuery('');
      setUrgencyQuery('');
    }
  }, [serviceRequest, isOpen]);

  const createServiceRequestMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/cases', data);
      return response.data;
    },
    onSuccess: async (data) => {
      // Extract the case ID from the response (handle different response structures)
      const caseId = data?.case?.id || data?.id;

      if (!caseId) {
        console.error('Failed to get case ID from response:', data);
        setErrorMessage('Service request created but failed to get ID');
        onClose();
        return;
      }

      // Create related record linking the duplicate to the original
      // Only create ONE directional relationship - the backend GET will show it bidirectionally
      try {
        console.log('[DuplicateServiceRequest] Creating single relationship:', {
          from: caseId,
          to: serviceRequest.id
        });

        await api.post(`/cases/${caseId}/related-records`, {
          relationships: [{
            targetId: serviceRequest.id,
            relationshipType: 'RELATED_TO'
          }]
        });

        console.log('[DuplicateServiceRequest] Successfully created relationship');
      } catch (error) {
        console.error('[DuplicateServiceRequest] Failed to create related record:', error);
      }

      queryClient.invalidateQueries({ queryKey: ['service-requests'] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['related-records', serviceRequest.id] });
      queryClient.invalidateQueries({ queryKey: ['related-records', caseId] });
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(data);
      }
      
      onClose();
      // Navigate to the new service request
      navigate(`/service-requests/${caseId}`);
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.message || 'Failed to create service request';
      setErrorMessage(errorMsg);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    const errors: Record<string, string> = {};

    if (!formData.impact) {
      errors.impact = 'Impact is required';
    }
    if (!formData.urgency) {
      errors.urgency = 'Urgency is required';
    }
    if (!formData.shortDescription.trim()) {
      errors.shortDescription = 'Short Description is required';
    }
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }
    if (!formData.customerId) {
      errors.customerId = 'Customer is required';
    }
    if (!formData.engagementId) {
      errors.engagementId = 'Engagement is required';
    }
    if (!formData.assignmentGroupId) {
      errors.assignmentGroupId = 'Assignment Group is required';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setErrorMessage('Please fill in all required fields');
      return;
    }

    setValidationErrors({});
    setErrorMessage(null);

    const payload: any = {
      caseType: 'service_request',
      ticketType: 'SERVICE_REQUEST',
      shortDescription: formData.shortDescription,
      description: formData.description,
      priority: formData.priority,
      impact: formData.impact,
      urgency: formData.urgency,
      assignedToId: formData.assignedTo,
      assignmentGroupId: formData.assignmentGroupId || undefined,
      customerId: formData.customerId,
      contactId: formData.contactId || undefined,
      engagementId: formData.engagementId,
      servicePlanId: formData.servicePlanId || undefined
    };

    // Handle resource fields based on isOtherResource
    if (isOtherResource) {
      payload.otherResourceNames = formData.otherResourceNames;
    } else {
      payload.resourceId = formData.resourceId || undefined;
      payload.resourceIdValue = formData.resourceIdValue || undefined;
    }

    createServiceRequestMutation.mutate(payload);
  };

  const customers = customersData?.customers || [];
  const contacts = contactsData?.users || [];
  const allEngagements = engagementsData?.engagements || [];
  const servicePlans = servicePlansData?.plans || [];

  // Filter engagements based on selected customer
  const engagements = formData.customerId
    ? allEngagements.filter((e: any) => e.Customer?.id === formData.customerId)
    : allEngagements;

  const groups = groupsData?.groups || [];
  const resources = Array.isArray(resourcesData) ? resourcesData : [];
  const groupUsers = usersData || [];

  // Filtered data for comboboxes
  const filteredImpactOptions = impactQuery === ''
    ? impactOptions
    : impactOptions.filter((option) =>
        option.label.toLowerCase().includes(impactQuery.toLowerCase())
      );

  const filteredUrgencyOptions = urgencyQuery === ''
    ? urgencyOptions
    : urgencyOptions.filter((option) =>
        option.label.toLowerCase().includes(urgencyQuery.toLowerCase())
      );

  const filteredCustomers = customerQuery === ''
    ? customers
    : customers.filter((c: any) =>
        c.accountName?.toLowerCase().includes(customerQuery.toLowerCase())
      );

  const filteredContacts = contactQuery === ''
    ? contacts
    : contacts.filter((c: any) => {
        const fullName = `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase();
        const email = (c.email || '').toLowerCase();
        return fullName.includes(contactQuery.toLowerCase()) || email.includes(contactQuery.toLowerCase());
      });

  const filteredEngagements = engagementQuery === ''
    ? engagements
    : engagements.filter((e: any) =>
        e.name?.toLowerCase().includes(engagementQuery.toLowerCase()) ||
        e.Customer?.accountName?.toLowerCase().includes(engagementQuery.toLowerCase())
      );

  const filteredServicePlans = servicePlanQuery === ''
    ? servicePlans
    : servicePlans.filter((p: any) =>
        (p.name || p.planName || '').toLowerCase().includes(servicePlanQuery.toLowerCase())
      );

  const filteredResources = resourceQuery === ''
    ? resources
    : resources.filter((r: any) =>
        (r.name || r.resourceId || '').toLowerCase().includes(resourceQuery.toLowerCase())
      );

  const filteredAssignmentGroups = assignmentGroupQuery === ''
    ? groups
    : groups.filter((g: any) =>
        g.name?.toLowerCase().includes(assignmentGroupQuery.toLowerCase())
      );

  const filteredGroupUsers = assignedToQuery === ''
    ? groupUsers
    : groupUsers.filter((u: any) => {
        const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
        const email = (u.email || '').toLowerCase();
        return fullName.includes(assignedToQuery.toLowerCase()) || email.includes(assignedToQuery.toLowerCase());
      });

  // Helper function to get priority label
  const getPriorityLabel = (priority: string): string => {
    const labels: Record<string, string> = {
      'critical_1': 'Critical',
      'high_2': 'High',
      'moderate_3': 'Moderate',
      'low_4': 'Low',
      'planning_5': 'Planning'
    };
    return labels[priority] || 'Moderate';
  };

  // Helper function to get impact/urgency label
  const getImpactUrgencyLabel = (value: string): string => {
    const option = impactOptions.find(o => o.value === value);
    return option ? option.label : '';
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Duplicate Service Request {serviceRequest?.number}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
                  {/* Error Message */}
                  {errorMessage && (
                    <div className="rounded-md bg-red-50 p-4">
                      <div className="text-sm text-red-700">{errorMessage}</div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Row 1: State and Impact */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* State (readonly) */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          State
                        </label>
                        <div className="flex-1">
                          <input
                            type="text"
                            value="New"
                            disabled
                            className="block w-full h-[38px] rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                          />
                        </div>
                      </div>

                      {/* Impact */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Impact <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={formData.impact}
                            onChange={(value) => {
                              setFormData({ ...formData, impact: value || '' });
                              if (validationErrors.impact) {
                                setValidationErrors(prev => ({ ...prev, impact: '' }));
                              }
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`block w-full h-[38px] rounded-md border ${validationErrors.impact ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                                displayValue={(value: string) => getImpactUrgencyLabel(value)}
                                onChange={(event) => setImpactQuery(event.target.value)}
                                placeholder="Search impact..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setImpactQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredImpactOptions.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No options found.
                                    </div>
                                  ) : (
                                    filteredImpactOptions.map((option) => (
                                      <Combobox.Option
                                        key={option.value}
                                        value={option.value}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {option.label}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.impact && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.impact}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Urgency and Priority */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Urgency */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Urgency <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={formData.urgency}
                            onChange={(value) => {
                              setFormData({ ...formData, urgency: value || '' });
                              if (validationErrors.urgency) {
                                setValidationErrors(prev => ({ ...prev, urgency: '' }));
                              }
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`block w-full h-[38px] rounded-md border ${validationErrors.urgency ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                                displayValue={(value: string) => getImpactUrgencyLabel(value)}
                                onChange={(event) => setUrgencyQuery(event.target.value)}
                                placeholder="Search urgency..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setUrgencyQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredUrgencyOptions.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No options found.
                                    </div>
                                  ) : (
                                    filteredUrgencyOptions.map((option) => (
                                      <Combobox.Option
                                        key={option.value}
                                        value={option.value}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {option.label}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.urgency && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.urgency}</p>
                          )}
                        </div>
                      </div>

                      {/* Priority (Auto-calculated) */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Priority
                        </label>
                        <div className="flex-1">
                          <input
                            type="text"
                            value={formData.priority ? getPriorityLabel(formData.priority) : ''}
                            disabled
                            className="block w-full h-[38px] rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                            placeholder="Auto-calculated from Impact & Urgency"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Row 3: Customer and Engagement */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Customer */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Customer <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={formData.customerId}
                            onChange={(value) => {
                              setFormData({ ...formData, customerId: value || '', contactId: '', engagementId: '', servicePlanId: '', resourceId: '', resourceIdValue: '', otherResourceNames: '' });
                              setSelectedResource(null);
                              setIsOtherResource(false);
                              if (validationErrors.customerId) {
                                setValidationErrors(prev => ({ ...prev, customerId: '' }));
                              }
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`block w-full h-[38px] rounded-md border ${validationErrors.customerId ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                                displayValue={(customerId: string) => {
                                  const customer = customers.find((c: any) => c.id === customerId);
                                  return customer ? customer.accountName : '';
                                }}
                                onChange={(event) => setCustomerQuery(event.target.value)}
                                placeholder="Search customers..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setCustomerQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredCustomers.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No customers found.
                                    </div>
                                  ) : (
                                    filteredCustomers.map((customer: any) => (
                                      <Combobox.Option
                                        key={customer.id}
                                        value={customer.id}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {customer.accountName}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.customerId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.customerId}</p>
                          )}
                        </div>
                      </div>

                      {/* Engagement */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Engagement <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={formData.engagementId}
                            onChange={(value) => {
                              setFormData({ ...formData, engagementId: value || '', servicePlanId: '', resourceId: '', resourceIdValue: '', otherResourceNames: '' });
                              setSelectedResource(null);
                              setIsOtherResource(false);
                              if (validationErrors.engagementId) {
                                setValidationErrors(prev => ({ ...prev, engagementId: '' }));
                              }
                            }}
                            disabled={!formData.customerId}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`block w-full h-[38px] rounded-md border ${validationErrors.engagementId ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                displayValue={(engagementId: string) => {
                                  const engagement = engagements.find((e: any) => e.id === engagementId);
                                  return engagement ? `${engagement.Customer?.accountName || ''} - ${engagement.name}` : '';
                                }}
                                onChange={(event) => setEngagementQuery(event.target.value)}
                                placeholder={formData.customerId ? 'Search engagements...' : 'Select customer first'}
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setEngagementQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredEngagements.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No engagements found.
                                    </div>
                                  ) : (
                                    filteredEngagements.map((engagement: any) => (
                                      <Combobox.Option
                                        key={engagement.id}
                                        value={engagement.id}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {engagement.Customer?.accountName || ''} - {engagement.name}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.engagementId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.engagementId}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 6: Contact and Service Plan */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Contact */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Contact
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={formData.contactId}
                            onChange={(value) => {
                              setFormData({ ...formData, contactId: value || '' });
                            }}
                            disabled={!formData.customerId}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className="block w-full h-[38px] rounded-md border border-gray-300 bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                                displayValue={(contactId: string) => {
                                  const contact = contacts.find((c: any) => c.id === contactId);
                                  if (!contact) return '';
                                  return contact.firstName && contact.lastName
                                    ? `${contact.firstName} ${contact.lastName}`
                                    : contact.email;
                                }}
                                onChange={(event) => setContactQuery(event.target.value)}
                                placeholder={formData.customerId ? 'Search contacts...' : 'Select customer first'}
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setContactQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredContacts.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No contacts found.
                                    </div>
                                  ) : (
                                    filteredContacts.map((contact: any) => (
                                      <Combobox.Option
                                        key={contact.id}
                                        value={contact.id}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {contact.firstName && contact.lastName
                                                ? `${contact.firstName} ${contact.lastName}`
                                                : contact.email}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                        </div>
                      </div>

                      {/* Service Plan */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Service Plan
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={formData.servicePlanId}
                            onChange={(value) => {
                              setFormData({ ...formData, servicePlanId: value || '' });
                            }}
                            disabled={!formData.engagementId}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className="block w-full h-[38px] rounded-md border border-gray-300 bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                                displayValue={(planId: string) => {
                                  const plan = servicePlans.find((p: any) => p.id === planId);
                                  return plan ? (plan.name || plan.planName) : '';
                                }}
                                onChange={(event) => setServicePlanQuery(event.target.value)}
                                placeholder={formData.engagementId ? 'Search service plans...' : 'Select engagement first'}
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setServicePlanQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredServicePlans.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No service plans available.
                                    </div>
                                  ) : (
                                    filteredServicePlans.map((plan: any) => (
                                      <Combobox.Option
                                        key={plan.id}
                                        value={plan.id}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {plan.name || plan.planName}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                        </div>
                      </div>
                    </div>

                    {/* Row 7: Resource and Resource ID/Names */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Resource */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Resource
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={isOtherResource ? '__OTHER__' : formData.resourceId}
                            onChange={(value) => {
                              if (value === '__OTHER__') {
                                setIsOtherResource(true);
                                setSelectedResource(null);
                                setFormData({ ...formData, resourceId: '', resourceIdValue: '', otherResourceNames: '' });
                              } else {
                                setIsOtherResource(false);
                                const resourcesArray = Array.isArray(resourcesData) ? resourcesData : [];
                                const resource = resourcesArray.find((r: any) => r.id === value);
                                if (resource) {
                                  setSelectedResource({
                                    id: resource.id,
                                    name: resource.name || '',
                                    resourceId: resource.resourceId || ''
                                  });
                                  setFormData({ ...formData, resourceId: value || '', resourceIdValue: resource.resourceId || '', otherResourceNames: '' });
                                } else {
                                  setFormData({ ...formData, resourceId: value || '', otherResourceNames: '' });
                                }
                              }
                            }}
                            disabled={!formData.engagementId}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className="block w-full h-[38px] rounded-md border border-gray-300 bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                                displayValue={(resourceId: string) => {
                                  if (isOtherResource) {
                                    return 'Others (specify custom resources)';
                                  }
                                  if (resourceId) {
                                    // First try to find in loaded resources
                                    const resource = resources.find((r: any) => r.id === resourceId);
                                    if (resource) return resource.name || resource.resourceId;
                                    // Fallback to selectedResource state
                                    if (selectedResource && selectedResource.id === resourceId) {
                                      return selectedResource.name;
                                    }
                                    // Fallback to serviceRequest's original resource data
                                    if (serviceRequest?.Resource && serviceRequest.Resource.id === resourceId) {
                                      return serviceRequest.Resource.name || '';
                                    }
                                  }
                                  return '';
                                }}
                                onChange={(event) => setResourceQuery(event.target.value)}
                                placeholder={formData.engagementId ? 'Search resources...' : 'Select engagement first'}
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setResourceQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredResources.length === 0 && resourceQuery !== '' ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No resources found.
                                    </div>
                                  ) : (
                                    <>
                                      {filteredResources.map((resource: any) => (
                                        <Combobox.Option
                                          key={resource.id}
                                          value={resource.id}
                                          className={({ active }) =>
                                            `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                              active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                            }`
                                          }
                                        >
                                          {({ active, selected }) => (
                                            <>
                                              <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                                {resource.name || resource.resourceId}
                                              </span>
                                              {selected && (
                                                <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                </span>
                                              )}
                                            </>
                                          )}
                                        </Combobox.Option>
                                      ))}
                                      {resourceQuery === '' && (
                                        <Combobox.Option
                                          value="__OTHER__"
                                          className={({ active }) =>
                                            `relative cursor-pointer select-none py-2 pl-3 pr-9 border-t border-gray-200 ${
                                              active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                            }`
                                          }
                                        >
                                          {({ active, selected }) => (
                                            <>
                                              <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                                Others (specify custom resources)
                                              </span>
                                              {selected && (
                                                <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                </span>
                                              )}
                                            </>
                                          )}
                                        </Combobox.Option>
                                      )}
                                    </>
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                        </div>
                      </div>

                      {/* Resource ID (readonly) or Resource Names (editable) */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          {isOtherResource ? 'Resource Names' : 'Resource ID'}
                        </label>
                        <div className="flex-1">
                          {isOtherResource ? (
                            <input
                              type="text"
                              value={formData.otherResourceNames}
                              onChange={(e) => setFormData({ ...formData, otherResourceNames: e.target.value })}
                              className="block w-full h-[38px] rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              placeholder="Enter custom resource names"
                            />
                          ) : (
                            <input
                              type="text"
                              value={formData.resourceIdValue}
                              disabled
                              className="block w-full h-[38px] rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                              placeholder="Auto-filled from Resource"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 7: Assignment Group and Assigned To */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Assignment Group */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Assignment Group <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={formData.assignmentGroupId}
                            onChange={(value) => {
                              setFormData({ ...formData, assignmentGroupId: value || '', assignedTo: '' });
                              if (validationErrors.assignmentGroupId) {
                                setValidationErrors(prev => ({ ...prev, assignmentGroupId: '' }));
                              }
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`block w-full h-[38px] rounded-md border bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                                  validationErrors.assignmentGroupId ? 'border-red-300' : 'border-gray-300'
                                }`}
                                displayValue={(groupId: string) => {
                                  const group = groups.find((g: any) => g.id === groupId);
                                  return group ? group.name : '';
                                }}
                                onChange={(event) => setAssignmentGroupQuery(event.target.value)}
                                placeholder="Search assignment groups..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setAssignmentGroupQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredAssignmentGroups.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No groups found.
                                    </div>
                                  ) : (
                                    filteredAssignmentGroups.map((group: any) => (
                                      <Combobox.Option
                                        key={group.id}
                                        value={group.id}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {group.name}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.assignmentGroupId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.assignmentGroupId}</p>
                          )}
                        </div>
                      </div>

                      {/* Assigned To */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Assigned To
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={formData.assignedTo}
                            onChange={(value) => {
                              setFormData({ ...formData, assignedTo: value || '' });
                            }}
                            disabled={!formData.assignmentGroupId}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`block w-full h-[38px] rounded-md border ${validationErrors.assignedTo ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                displayValue={(userId: string) => {
                                  const user = groupUsers.find((u: any) => u.id === userId);
                                  return user ? (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email) : '';
                                }}
                                onChange={(event) => setAssignedToQuery(event.target.value)}
                                placeholder={formData.assignmentGroupId ? 'Search assignees...' : 'Select assignment group first'}
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setAssignedToQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredGroupUsers.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No assignees found.
                                    </div>
                                  ) : (
                                    filteredGroupUsers.map((user: any) => (
                                      <Combobox.Option
                                        key={user.id}
                                        value={user.id}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.assignedTo && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.assignedTo}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 8: Short Description */}
                    <div className="flex items-start gap-3">
                      <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                        Short Description <span className="text-red-500">*</span>
                      </label>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={formData.shortDescription}
                          onChange={(e) => {
                            setFormData({ ...formData, shortDescription: e.target.value });
                            if (validationErrors.shortDescription) {
                              setValidationErrors(prev => ({ ...prev, shortDescription: '' }));
                            }
                          }}
                          className={`block w-full h-[38px] rounded-md border ${validationErrors.shortDescription ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'} shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                          placeholder="Enter short description..."
                        />
                        {validationErrors.shortDescription && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.shortDescription}</p>
                        )}
                      </div>
                    </div>

                    {/* Row 9: Description (Rich Text Editor) */}
                    <div className="flex items-start gap-3">
                      <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <div className="flex-1">
                        <RichTextEditor
                          value={formData.description}
                          onChange={(value) => {
                            setFormData({ ...formData, description: value });
                            if (validationErrors.description) {
                              setValidationErrors(prev => ({ ...prev, description: '' }));
                            }
                          }}
                          placeholder="Enter description..."
                          minHeight="120px"
                          className={validationErrors.description ? 'border-red-300' : ''}
                        />
                        {validationErrors.description && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.description}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createServiceRequestMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400"
                    >
                      {createServiceRequestMutation.isPending ? 'Duplicating...' : 'Duplicate Service Request'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DuplicateServiceRequestModal;
